/**
 * Сборка промпта с вариацией демо «до/после»:
 *   • в базовом промпте есть плейсхолдер {variant} — заменяет его вставкой;
 *   • иначе — дописывает вставку через запятую в конец.
 * Используется демо-модалом стилей и демо-блоком «Популярных».
 */
import { PROMPT_VARS_SPEC, promptVar, varMax, varPhrase } from '../prompt-vars'

export function buildVariantPrompt(base: string, patch: string): string {
	const b = (base || '').trim()
	const p = (patch || '').trim()
	if (!p) return b
	if (b.includes('{variant}')) return b.replace(/\{variant\}/g, p)
	return b ? `${b}, ${p}` : p
}

/**
 * Сборка промпта open-вариации («Свой вариант» — значение вписывает юзер):
 *   • шаблон вставки с {value} — ввод встаёт в шаблон, шаблон → на {variant};
 *   • ПУСТОЙ шаблон — ввод подставляется как есть (как обычная вариация);
 *   • {value} прямо в базовом промпте — тоже заменяется вводом (админ волен
 *     размещать плейсхолдер в базе), пустой ввод вычищает плейсхолдер.
 */
export function buildOpenVariantPrompt(base: string, template: string, value: string): string {
	const v = (value || '').trim()
	const tpl = (template || '').trim()
	const patch = v ? (tpl ? tpl.replace(/\{value\}/g, v) : v) : ''
	return buildVariantPrompt(base, patch).replace(/\{value\}/g, v)
}

/**
 * Слот-переменные фото-слотов карточки (владелец 2026-08-14): {photo1}…{photo30}
 * в промпте заменяются позиционными фразами — модель получает команду «на каком
 * фото что лежит», а админ не зависит от точной формулировки. Замена идёт при
 * ПРИМЕНЕНИИ карточки (юзер в баре уже видит человеческий текст); переменные
 * без слотов тоже заменяются — мусор `{photo1}` не должен утечь в модель.
 */
// Токены и фразы — из общего определения переменных (prompt-vars/spec.json): те же
// токены рисует конструктор карточек в админке, те же фразы переводит сервер в синтаксис
// провайдера. Техническое имя «Image N» (владелец 04.09, как у Kling) едино для фото- и
// видео-моделей; оно же подпись пилюли в баре (promptPillPhrases).
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
/** /\{photo(\d{1,2})\}/g — из шаблона токена '{photo{n}}'; номер сверх потолка спеки (photo.max, 30 с 18.10) —
 *  не слот: такой токен остаётся текстом. Класс символов «[1-N]» здесь нельзя — от десяти он ломается. */
const PHOTO_TOKEN_RE = new RegExp(esc(promptVar('photo').token).replace(/\\\{n\\\}/, '(\\d{1,2})'), 'g')
const PHOTO_MAX = varMax('photo')
const slotNum = (raw: string): number | null => {
	const n = Number(raw)
	return n >= 1 && n <= PHOTO_MAX ? n : null
}

/** Номера слотов, которые текст реально использует (уникальные, по порядку). */
export function usedPhotoSlotNums(text: string): number[] {
	const out: number[] = []
	for (const m of (text || '').matchAll(PHOTO_TOKEN_RE)) {
		const n = slotNum(m[1])
		if (n != null && !out.includes(n)) out.push(n)
	}
	return out.sort((a, b) => a - b)
}

export function substitutePhotoSlots(prompt: string, kind: 'image' | 'video' = 'image'): string {
	// Позиционные фразы — ПО РАНГУ среди использованных номеров, не по самим
	// номерам: стиль мультистиля может брать лишь часть слотов (владелец
	// 2026-08-16), и тогда единственная картинка — «Image 1», даже
	// если это слот №2. Виджет собирает referenceImages тем же порядком
	// (использованные слоты по возрастанию) — промпт и файлы не разъезжаются.
	const used = usedPhotoSlotNums(prompt)
	let out = (prompt || '').replace(PHOTO_TOKEN_RE, (m, raw) => {
		const n = slotNum(raw)
		if (n == null) return m
		return varPhrase('photo', kind, used.indexOf(n) >= 0 ? used.indexOf(n) + 1 : n)
	})
	// Переменные ЖЁСТКИХ полей (владелец 2026-08-20) и референсов карточки
	// «под капотом» ({video}, {audio}, 25.09) — опциональные ссылки на входы:
	// поле работает и без упоминания, фраза лишь позволяет промпту сослаться на
	// его контент (как {photoN} у промпт-слотов).
	for (const v of PROMPT_VARS_SPEC.vars) {
		if (v.numbered || !v.phrase) continue
		out = out.replace(new RegExp(esc(v.token), 'g'), varPhrase(v.key, kind))
	}
	return out
}

// ── значения полей карточки и незаполненные токены (аудит мультистилей 22.10) ──────────────────────────────

/** Что нужно от пина/карточки, чтобы понять, какое значение какого поля сейчас активно. */
export interface VariantValuesSource {
	activeVariantKey?: string | null
	/** «Свой вариант»: текст open-вариации. */
	customValue?: string | null
	/** Значения полей по ключу: '__card__' — общее поле карточки, key стиля — его своё поле. */
	userValues?: Record<string, string> | null
	/** Легаси: значение общего поля одной строкой (пины до Ф10). */
	userValue?: string | null
	user_input?: unknown
	demoVariants?: Array<{ key: string; is_open?: boolean; user_input?: unknown }> | null
}

/** Значение поля стиля `key`: своё поле стиля → userValues[key]; общее поле карточки → userValues.__card__ (легаси
 *  userValue); поля нет → ''. Та же логика, что fieldCfgFor в VariantContext — для пина без контекста (после F5 и в
 *  запросе): восстановление читало только легаси userValue, и введённое значение в промпт не попадало. */
export function variantFieldValue(src: VariantValuesSource, key: string | null | undefined): string {
	const chip = (src.demoVariants ?? []).find((v) => v.key === key)
	if (!chip) return ''
	if (chip.user_input) return (src.userValues?.[chip.key] ?? '').trim()
	if (src.user_input) return (src.userValues?.__card__ ?? src.userValue ?? '').trim()
	return ''
}

/** Текст, который человек ввёл сам: у «Своего варианта» — его значение, у стиля с полем — значение поля. Уходит на
 *  сервер полем user_prompt (история, сверка с карточкой). До 22.10 уходил только текст «Своего варианта», и тот
 *  залипал после выбора обычного стиля. */
export function variantUserText(src: VariantValuesSource): string {
	const key = src.activeVariantKey || (src.demoVariants ?? [])[0]?.key
	const chip = (src.demoVariants ?? []).find((v) => v.key === key)
	if (chip?.is_open) return (src.customValue ?? '').trim()
	return variantFieldValue(src, key)
}

/** В промпте остались токены карточки ({variant}, {value}, {photoN}) — такой запрос отправлять нельзя: нейросеть
 *  получит их текстом, а человек заплатит (аудит 22.10: по ссылке на инструмент уходила база с {variant}). */
export function hasUnresolvedCardTokens(prompt: string | null | undefined): boolean {
	return /\{(?:variant|value|photo\d{1,2})\}/.test(prompt || '')
}
